import { type CSSProperties, type ReactNode } from "react";
import { CONTACT_EMAIL, useI18n, type Locale } from "@/lib/i18n";

/** A Material Symbols (Outlined) glyph. `filled` swaps to the filled
 *  axis — used for active/selected states. */
export function Icon({
  name,
  className,
  filled,
  style,
}: {
  name: string;
  className?: string;
  filled?: boolean;
  style?: CSSProperties;
}) {
  return (
    <span
      aria-hidden
      className={`material-symbols-outlined ${className ?? ""}`}
      style={filled ? { fontVariationSettings: "'FILL' 1", ...style } : style}
    >
      {name}
    </span>
  );
}

/** The Microsoft four-square mark for the sign-in buttons. */
export function MicrosoftLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 21 21"
      className={className ?? "h-4 w-4"}
      aria-hidden
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function Button({
  children,
  onClick,
  variant = "primary",
  disabled,
  fullWidth = false,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary";
  disabled?: boolean;
  fullWidth?: boolean;
}) {
  const styles = {
    primary: "bg-primary text-on-primary hover:opacity-90",
    secondary:
      "border border-outline-variant bg-surface-container-lowest text-on-surface hover:bg-surface-container-highest",
  }[variant];
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-button-text transition disabled:opacity-50 ${
        fullWidth ? "w-full" : ""
      } ${styles}`}
    >
      {children}
    </button>
  );
}

export function Card({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-outline-variant bg-surface-container-lowest p-8 ${
        className ?? ""
      }`}
    >
      {children}
    </div>
  );
}

export function Spinner() {
  return (
    <div
      className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-outline-variant border-t-primary"
      role="status"
      aria-label="loading"
    />
  );
}

/** A status icon at the top of a wizard screen. */
export function ScreenIcon({ tone }: { tone: "ok" | "warn" | "info" }) {
  const map = {
    ok: { bg: "bg-emerald-100", fg: "text-emerald-700", icon: "check_circle" },
    warn: {
      bg: "bg-tertiary-fixed",
      fg: "text-on-tertiary-fixed-variant",
      icon: "warning",
    },
    info: { bg: "bg-primary-fixed", fg: "text-primary", icon: "info" },
  }[tone];
  return (
    <div
      className={`mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full ${map.bg}`}
    >
      <Icon name={map.icon} filled className={`text-[24px] ${map.fg}`} />
    </div>
  );
}

/** PT / EN locale switch, shared by both shells. */
export function LanguageToggle() {
  const { locale, setLocale } = useI18n();
  const opt = (l: Locale, label: string) => (
    <button
      type="button"
      onClick={() => setLocale(l)}
      className={[
        "px-1 text-label-muted transition-colors",
        locale === l
          ? "font-semibold text-on-surface"
          : "text-on-surface-variant hover:text-on-surface",
      ].join(" ")}
    >
      {label}
    </button>
  );
  return (
    <div className="flex items-center text-label-muted text-on-surface-variant">
      {opt("pt-BR", "PT")}
      <span aria-hidden>/</span>
      {opt("en", "EN")}
    </div>
  );
}

/** Wizard shell: brand header, centered content column, legal/help footer.
 *  Chrome-free by design — the onboarding ceremony carries no sidebar. */
export function Layout({ children }: { children: ReactNode }) {
  const { t } = useI18n();
  return (
    <div className="flex min-h-full flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-4">
        <a href="/" className="text-page-title font-bold text-on-surface">
          Finn
        </a>
        <LanguageToggle />
      </header>

      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>

      <footer className="flex flex-col items-center gap-2 px-6 py-6 text-label-muted text-on-surface-variant sm:flex-row sm:justify-between">
        <div className="flex gap-4">
          <a href="/privacy" className="hover:text-on-surface">
            {t("footer.privacy")}
          </a>
          <a href="/terms" className="hover:text-on-surface">
            {t("footer.terms")}
          </a>
          <a href="/help/install-teams" className="hover:text-on-surface">
            {t("footer.install")}
          </a>
        </div>
        <div>© Aton</div>
      </footer>
    </div>
  );
}

/** Shared contact-Aton line used by the failure screens. */
export function ContactLine() {
  const { t } = useI18n();
  return (
    <p className="mt-4 text-body-sm text-on-surface-variant">
      {t("problem.contact", { email: CONTACT_EMAIL })}
    </p>
  );
}
